import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useCreateClient } from '../../hooks/useClients';

// Stricter email validation regex (requires local@domain.tld format)
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const createClientSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z
    .string()
    .min(1, 'Email is required')
    .refine(
      (email) => emailRegex.test(email.trim()),
      { message: 'Please enter a valid email address' }
    ),
  phone: z.string().optional(),
  company: z.string().optional(),
  jobTitle: z.string().optional(),
  status: z.enum(['active', 'inactive', 'lead']).default('active'),
  source: z.enum(['referral', 'social_media', 'website', 'google', 'instagram', 'linkedin', 'facebook', 'tiktok', 'word_of_mouth', 'other']).optional(),
  sourceDetails: z.string().optional(),
  tags: z.string().optional(),
});

type CreateClientFormData = z.infer<typeof createClientSchema>;

interface CreateClientModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Capitalize first letter of a name
 * "bob" -> "Bob", "BOB" -> "Bob"
 */
function capitalizeFirstLetter(str: string): string {
  if (!str) return str;
  return str.trim().charAt(0).toUpperCase() + str.trim().slice(1).toLowerCase();
}

export default function CreateClientModal({ open, onClose }: CreateClientModalProps) {
  const [error, setError] = useState<string | null>(null);
  const createClient = useCreateClient();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateClientFormData>({
    resolver: zodResolver(createClientSchema),
    defaultValues: {
      status: 'active',
    },
  });

  const onSubmit = async (data: CreateClientFormData) => {
    setError(null);

    try {
      const tags = data.tags
        ? data.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [];

      await createClient.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
        contact: {
          email: data.email,
          phone: data.phone,
          preferredContact: 'email',
        },
        company: data.company,
        jobTitle: data.jobTitle,
        status: data.status,
        source: data.source || undefined,
        sourceDetails: data.sourceDetails,
        tags,
      });

      reset();
      onClose();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'Failed to create client');
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add New Client</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Name */}
            <Grid item xs={6}>
              <TextField
                {...register('firstName')}
                label="First Name *"
                fullWidth
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                onBlur={(e) => {
                  const capitalized = capitalizeFirstLetter(e.target.value);
                  if (capitalized !== e.target.value) {
                    setValue('firstName', capitalized);
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                {...register('lastName')}
                label="Last Name *"
                fullWidth
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                onBlur={(e) => {
                  const capitalized = capitalizeFirstLetter(e.target.value);
                  if (capitalized !== e.target.value) {
                    setValue('lastName', capitalized);
                  }
                }}
              />
            </Grid>

            {/* Contact */}
            <Grid item xs={12}>
              <TextField
                {...register('email')}
                label="Email *"
                type="text"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register('phone')}
                label="Phone"
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone?.message}
              />
            </Grid>

            {/* Company */}
            <Grid item xs={6}>
              <TextField
                {...register('company')}
                label="Company"
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                {...register('jobTitle')}
                label="Job Title"
                fullWidth
              />
            </Grid>

            {/* Status & Source */}
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  {...register('status')}
                  label="Status"
                  defaultValue="active"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="lead">Lead</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  {...register('source')}
                  label="Source"
                  defaultValue=""
                >
                  <MenuItem value="">Not specified</MenuItem>
                  <MenuItem value="referral">Referral</MenuItem>
                  <MenuItem value="social_media">Social Media</MenuItem>
                  <MenuItem value="website">Website</MenuItem>
                  <MenuItem value="google">Google</MenuItem>
                  <MenuItem value="instagram">Instagram</MenuItem>
                  <MenuItem value="linkedin">LinkedIn</MenuItem>
                  <MenuItem value="facebook">Facebook</MenuItem>
                  <MenuItem value="tiktok">TikTok</MenuItem>
                  <MenuItem value="word_of_mouth">Word of Mouth</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Source Details */}
            <Grid item xs={12}>
              <TextField
                {...register('sourceDetails')}
                label="Source Details"
                fullWidth
                placeholder="e.g., Referred by Alex Thompson"
              />
            </Grid>

            {/* Tags */}
            <Grid item xs={12}>
              <TextField
                {...register('tags')}
                label="Tags"
                fullWidth
                placeholder="VIP, Brand Coaching, Social Media (comma-separated)"
                helperText="Separate tags with commas"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Client'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
